import React, { useState } from 'react'
import { Button, Dialog, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material'
import { useAppDispatch } from '../../../../app/hooks'
import { useUpdateEmailAddressMutation } from '../../../../api/asset-holders/assetHolderApi'
import {
  type AssetHolderUpdateDto,
  type UpdateAssetHolderEmailAddressForm,
  type UpdateAssetHolderEmailAddressPayload
} from '../../../../api/schema-dtos/AssetHolderCrudDtos'
import { Formik } from 'formik'
import ErrorComponent from '../../../shared/components/ui-widgets/ErrorComponent'
import ButtonLoadingComponent from '../../../shared/components/ui-widgets/ButtonLoadingComponent'
import { setNotification } from '../../../shared/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import OtpComponent from '../../../auth/components/OtpComponent'

interface UpdateEmailAddressComponentProps {
  assetHolderDetails: AssetHolderUpdateDto
}

const UpdateEmailAddressComponent: React.FC<UpdateEmailAddressComponentProps> = ({ assetHolderDetails }: UpdateEmailAddressComponentProps) => {
  const dispatch = useAppDispatch()
  const [openDialog, toggleDialog] = useState<boolean>(false)
  const [otp, setOtp] = useState<string>('')

  const [apiUpdateEmail, updateEmailApiResult] = useUpdateEmailAddressMutation()

  const defaultForm: UpdateAssetHolderEmailAddressForm = {
    emailAddress: '',
    password: ''
  }

  const handleUpdateEmail = (payload: UpdateAssetHolderEmailAddressPayload): void => {
    console.log('changing email address')
    apiUpdateEmail(payload)
      .unwrap()
      .then(async () => {
        toggleDialog(false)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  return (
    <>
      <Button
        variant={'outlined'}
        color={'primary'}
        sx={{ mr: 2, mt: 2 }}
        onClick={() => {
          toggleDialog(true)
        }}
      >
        Update Email Address
      </Button>

      <Dialog
        open={openDialog}
        maxWidth={'md'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => {
          toggleDialog(false)
        }}
        sx={{ p: 2 }}
      >
        <DialogTitle variant={'h3'}>
          Change Email Address
        </DialogTitle>

        <DialogContent>
          <Formik
            initialValues={defaultForm}
            onSubmit={(values) => {
              handleUpdateEmail({
                emailAddress: values.emailAddress,
                assetHolderId: assetHolderDetails.assetHolderId,
                password: values.password,
                otp
              })
            }}
          >
            {formikProps => (
              <form
                autoComplete={'off'}
                autoCorrect={'off'}
                onSubmit={formikProps.handleSubmit}
              >
                <Grid container spacing={2}>
                  <Grid item md={12}>
                    <Typography variant={'body1'}>Please provide your new email address. For security reasons, you are required to provide your password when changing your email address.</Typography>
                  </Grid>

                  {!(formikProps.isValid) && (
                    <Grid item md={12} sx={{ mt: 3 }}>
                      <ErrorComponent title={'Form validation error'} message={Object.values(formikProps.errors).join(', ')}/>
                    </Grid>
                  )}
                  <Grid item md={12} xs={12}>
                    <TextField
                      label={'Email Address'}
                      type={'email'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      placeholder={'Email Address'}
                      {...formikProps.getFieldProps('emailAddress')}
                    />
                  </Grid>
                  <Grid item md={12} xs={12}>
                    <TextField
                      label={'Password'}
                      type={'password'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      placeholder={'Password'}

                      {...formikProps.getFieldProps('password')}
                    />
                  </Grid>
                  <Grid item md={12} xs={12} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <OtpComponent
                      assetHolderId={assetHolderDetails.assetHolderId}
                      actionName={'Change Email'}
                      isLoading={updateEmailApiResult.isLoading}
                      disabled={!formikProps.isValid}
                      authenticatorConfigured={assetHolderDetails.twoFAStatus}
                      executeAction={async (otp: string) => {
                        setOtp(otp)
                        await formikProps.submitForm()
                      }}
                    />

                    <Button
                      variant={'outlined'}
                      type="button"
                      onClick={() => { toggleDialog(false) }}
                      sx={{ ml: 2 }}
                      disabled={updateEmailApiResult.isLoading}
                    >
                      Cancel
                      {(updateEmailApiResult.isLoading && <ButtonLoadingComponent/>)}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default UpdateEmailAddressComponent
